/**
 * Shared types for the 3D fuel layer's planting pipeline (issue #276).
 *
 * The planting loop (millions of grid-cell candidates → instance transforms)
 * runs in a Web Worker, so every input/output here is structured-clone friendly:
 * plain objects + typed arrays, no THREE / mapbox objects. Typed arrays are
 * transferred (zero-copy) in both directions.
 */

import type { GroundCoverSpec, StandSpec } from "../fuelClassification";

export type Bbox = [number, number, number, number];

/**
 * One (Multi)Polygon in mercator units, flattened: `coords` is [x0,y0,x1,y1,…]
 * across all rings of all member polygons; `ringStarts` holds the point index
 * where each ring begins plus a final end sentinel; `polyStarts` holds the ring
 * index where each member polygon begins plus a final end sentinel. `bboxes` is
 * 4 numbers per member polygon.
 */
export interface PolySet {
  coords: Float64Array;
  ringStarts: Uint32Array;
  polyStarts: Uint32Array;
  bboxes: Float64Array;
}

/** A treed stand: the classification spec + an index into `polySets`. */
export interface StandInput extends StandSpec {
  polySet: number;
}

/** A ground-cover stand (may be grass-only): spec + polySet index. */
export interface GroundStandInput {
  ground: GroundCoverSpec;
  polySet: number;
}

/** Coarse terrain grid over the view (NaN where the DEM tile is missing). */
export interface ElevGridData {
  x0: number;
  y0: number;
  dx: number;
  dy: number;
  n: number;
  vals: Float32Array;
}

/** Fire arrival/peak-flame grid over the view (Infinity = unburnt cell). */
export interface FireGridData {
  x0: number;
  y0: number;
  dx: number;
  dy: number;
  n: number;
  arrival: Float32Array;
  flame: Float32Array;
}

/**
 * Explicit crowns: individual trees / shrubs with a known centre, crown
 * radius and height (the project's Crown rows, packed from the fine-detail
 * Tree / Shrub polygons). Drawn exactly where the imagery has them instead of
 * being planted on the lattice. `fireArrival` is baked per crown: seconds on
 * the animation clock for crowns the urban graph ignited, `FIRE_NEVER` for
 * ones it never reached, or `CROWN_FIRE_FROM_GRID` to sample the fire grid
 * like a lattice tree (clump interiors, which the graph does not model).
 */
export interface CrownSet {
  /** Mercator x/y per crown. */
  x: Float64Array;
  y: Float64Array;
  radiusM: Float32Array;
  heightM: Float32Array;
  /** 1 tree, 2 shrub. */
  kind: Uint8Array;
  fireArrival: Float32Array;
}

export interface PlantRequest {
  gen: number;
  /** Imagery-placed crowns; null when the project has none. */
  crowns?: CrownSet | null;
  /** Padded view bbox in mercator units. */
  view: Bbox;
  /**
   * Ground-projected view quad (bl, br, tr, tl → 8 numbers, mercator), padded.
   * Null when a corner unprojected to sky. Cells outside it are culled — at
   * high pitch the view bbox covers ~2x the visible trapezoid.
   */
  quad: Float64Array | null;
  /** Scene origin (mercator) + metres→mercator scale at the map centre. */
  refX: number;
  refY: number;
  scale: number;
  /** Camera ground position (mercator) + altitude (m) — the density taper and
   *  far-LOD split key on true 3D distance from the camera, so the foreground
   *  at high pitch is never thinned (the map centre sits far ahead of it). */
  camX: number;
  camY: number;
  camAltM: number;
  zoom: number;
  terrainOn: boolean;
  elev: ElevGridData | null;
  fire: FireGridData | null;
  polySets: PolySet[];
  stands: StandInput[];
  groundStands: GroundStandInput[];
  wantGround: boolean;
  showSurface: boolean;
  showGrass: boolean;
  maxTrees: number;
  maxGround: number;
  /** Scales tufts-per-grass-cell down on high-DPR displays (fill cost). */
  grassClumpScale: number;
  /**
   * Uniform geometry exaggeration for zoomed-out views (1 = real size). Below
   * ~z15 real trees are sub-pixel, so they are scaled to hold a minimum screen
   * height — the stylised model-forest look. Fire logic still uses real
   * heights, and exaggerated trees bake as low-poly ground-based crowns.
   */
  sizeMul: number;
  /**
   * Distance-tapered density: full density within this 3D camera distance
   * (metres), then a continuous falloff keep = (taperD0/d)^1.5 (floored at
   * 0.1) — near trees are never thinned, far sub-pixel trees are thinned
   * hard. 0 disables the taper. The expected-count estimate applies the same
   * factor so the uniform density backoff no longer eats the near field.
   */
  taperD0: number;
  /** Beyond this 3D camera distance (m) crowns go to the far (low-poly)
   *  buckets — altitude counts, so zoomed-out views go low-poly wholesale. */
  farDistM: number;
  /** Skip trunks in the far field (sub-pixel there by construction). */
  dropFarTrunks: boolean;
}

/**
 * One instanced bucket, ready for the GPU: column-major 4x4 matrices (16
 * floats/instance) composed in the worker, linear-space colours (3/instance),
 * and — on crown buckets — per-instance fire arrival seconds (1e30 = never
 * burns) and whether the cell's peak flames reached the crown (0/1).
 * Instances are sorted by per-tree priority so a single `count` cut keeps
 * whole trees (the movement LOD).
 */
export interface BucketResult {
  count: number;
  matrices: Float32Array;
  colors: Float32Array;
  fireArrival?: Float32Array;
  fireReached?: Float32Array;
}

export interface PlantResult {
  gen: number;
  /** True when DEM tiles were missing under some plants — caller retries. */
  terrainMissing: boolean;
  /** Scene-space bounds [minX,maxX,minZ,maxZ,minY,maxY] for the sun fit. */
  bounds: [number, number, number, number, number, number];
  trunks: BucketResult;
  conesNear: BucketResult;
  conesFar: BucketResult;
  blobsNear: BucketResult;
  blobsFar: BucketResult;
  litter: BucketResult;
  logs: BucketResult;
  nsShrubs: BucketResult;
  grass: BucketResult;
  treeCount: number;
  /** Explicit crowns drawn (a subset of `treeCount`). */
  crownCount: number;
  groundCount: number;
  capped: boolean;
  /** Worker-side planting time (ms) for the perf HUD. */
  plantMs: number;
}

/** Sentinel for "this cell never burns" (shader-friendly stand-in for Inf). */
export const FIRE_NEVER = 1e30;
/** `CrownSet.fireArrival` sentinel: take the fire from the grid, as a lattice tree does. */
export const CROWN_FIRE_FROM_GRID = -1;
